import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token refresh interval (25 minutes - before 30 min expiry)
const REFRESH_INTERVAL = 25 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  // Setup axios interceptor for auto-refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const detail = error.response?.data?.detail;
        
        // Single-device login: this session was replaced by a login on another device
        if (error.response?.status === 401 && detail === 'SESSION_REVOKED') {
          forceLogout('You were logged out because your account signed in on another device.');
          return Promise.reject(error);
        }
        
        // If 401 and we have refresh token and haven't retried
        if (error.response?.status === 401 && refreshToken && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const newTokens = await refreshAccessToken();
            if (newTokens) {
              // Retry original request with new token
              originalRequest.headers['Authorization'] = `Bearer ${newTokens.access_token}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, logout
            logout();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshToken]);

  // Refresh access token
  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) return null;
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        body_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken, user: userData } = response.data;
      
      // Update tokens
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', newRefreshToken);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setRefreshToken(newRefreshToken);
      setUser(userData);
      
      return { access_token, refresh_token: newRefreshToken };
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error.response?.data?.detail === 'SESSION_REVOKED') {
        forceLogout('You were logged out because your account signed in on another device.');
      }
      return null;
    }
  }, [refreshToken]);

  // Setup automatic token refresh
  useEffect(() => {
    if (token && refreshToken) {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      
      // Setup new refresh timer
      refreshTimerRef.current = setInterval(() => {
        refreshAccessToken();
      }, REFRESH_INTERVAL);
    }
    
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [token, refreshToken, refreshAccessToken]);

  // Initial auth check
  useEffect(() => {
    // Legacy: earlier builds persisted the user profile (name/phone/email) here
    localStorage.removeItem('user');
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      
      // On 401, try refresh token
      if (error.response?.status === 401 && refreshToken) {
        const newTokens = await refreshAccessToken();
        if (!newTokens) {
          logout();
        }
      } else if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (phone, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { phone, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Store tokens
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  // Emergent-managed Google sign-in: exchange the one-time session_id for an app session
  const loginWithGoogleSession = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/api/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    const { access_token, refresh_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);

    return userData;
  };

  const register = async (name, phone, password, role = 'staff', email = null) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      phone,
      password,
      role,
      email,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = useCallback(() => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    
    // Call backend logout to clear cookie + active session (needs the token)
    axios.post(`${API_URL}/api/auth/logout`).catch(() => {});
    
    // Clear storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Clear axios header
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset state
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  // Local-only logout used when the server already invalidated this session
  const forceLogout = useCallback((message) => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    if (message) {
      localStorage.setItem('authNotice', message);
    }
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }, []);

  const isAdmin = () => user?.role === 'admin';
  const isStaff = () => user?.role === 'staff';
  const isCaretaker = () => user?.role === 'caretaker';
  const canManage = () => user?.role === 'admin' || user?.role === 'staff';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        loginWithGoogleSession,
        isAdmin,
        isStaff,
        isCaretaker,
        canManage,
        refreshAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
